import { Button, BUTTON_TYPES } from '../../../components/Button'
import { BTN_TXT, OPTIONS_SORT_ORDER, PLACEHOLDERS } from '../../../constants'
import { Popup } from '../../../components/Popup'
import { useContractCategoryDetailsPopup } from '../Providers/CategoriesPopupProvider'
import { Typography } from '../../../components/Typography'
import '../style.scss'
import Select from '../../../components/Select/Select'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IPartialUserInfo } from '../../../redux/store/user/types'
import { useSelect } from '../../Reassign/useSelect'
import { getTeamResponsibleForReassign, reassignResponsible } from '../api'
import { notification } from '../../../components/Notification'
import { useDispatch, useSelector } from 'react-redux'
import { setLoading } from '../../../redux/store/common/slice'
import { TOption } from '../../../components/Select/types'
import { parseUserInfoForPicker } from './helpers'
import { fetchHealthSystemsList } from '../../HealthSystems/api'
import { getUser } from '../../../redux/store/user/getters'
import { formatOptionNameValue } from 'helper/optionsFormatters'

export const AssignCOLevelResponsiblePopup = ({
  onAssign
}: {
  onAssign: () => void
}) => {
  const user = useSelector(getUser)
  const [teamResponsibleOptions, setTeamResponsibleOptions] = useState<
    IPartialUserInfo[] | null
  >(null)
  const [filteredOptions, setFilteredOptions] = useState<IPartialUserInfo[]>([])
  const [hsOptions, setHsOptions] = useState<TOption[]>([])
  const [sourcingTeamUsers, setSourcingTeamUsers] = useState<
    string[] | undefined
  >()
  const [selectedHs, setSelectedHs] = useState<string | undefined>()
  const [sourcingOwner, setSourcingOwner] = useState<string | undefined>()
  const {
    addResponsibleUserPopup: {
      actions: { close },
      state: { visible, title, payload }
    }
  } = useContractCategoryDetailsPopup()
  const { selectedCC } = payload
  const dispatch = useDispatch()

  const { returnSelectUsers, handleSearch } = useSelect({
    users: teamResponsibleOptions ?? [],
    setFilteredOptions
  })

  // sourcing owner options are NOT available when we don't have any sourcing_owner value predefined already
  const sourcingOwnerOptions: TOption[] | null = useMemo(
    () =>
      !!selectedCC?.sourcing_owner
        ? (teamResponsibleOptions ?? [])
            .filter(
              (item) =>
                sourcingTeamUsers && sourcingTeamUsers.indexOf(item.uuid) !== -1
            )
            .map(parseUserInfoForPicker)
        : null,
    [selectedCC?.sourcing_owner, teamResponsibleOptions, sourcingTeamUsers]
  )

  const fetchTeamResponsiblesList = useCallback(
    async (hsId?: string) => {
      if (hsId) {
        // reset to enable picker no data screen
        setFilteredOptions([])
        // set to null to enable picker loader
        setTeamResponsibleOptions(null)
        const res = await getTeamResponsibleForReassign({
          uuid: selectedCC.uuid,
          hsId
        })
        if (res?.data) {
          setTeamResponsibleOptions(res.data?.results)
          setFilteredOptions(res.data?.results)
        }
      }
    },
    [selectedCC]
  )

  const fetchHSList = useCallback(async () => {
    if (user.community) {
      const res = await fetchHealthSystemsList(user.community)
      if (res?.data) {
        setHsOptions(res.data?.results.map(formatOptionNameValue) ?? [])
      }
    }
  }, [user])

  const onHsChange = useCallback(
    async (hsId: string) => {
      // resetting all selected values and re-fetching team responsibles list
      setSourcingTeamUsers(undefined)
      setSourcingOwner(undefined)
      setSelectedHs(hsId)
      await fetchTeamResponsiblesList(hsId)
    },
    [fetchTeamResponsiblesList]
  )

  const onTeamResponsibleChangeChange = useCallback(
    async (teamRespIds: string[]) => {
      // resetting sourcingOwner if it was removed from selected team responsibles
      // if no sourcingOwner selected - then there's no need to reset anything
      if (
        !teamRespIds.length ||
        teamRespIds.indexOf(sourcingOwner ?? '') === -1
      ) {
        setSourcingOwner(undefined)
      }
      setSourcingTeamUsers(teamRespIds)
    },
    [sourcingOwner]
  )

  const onCancel = useCallback(() => {
    close()
    setSourcingTeamUsers(undefined)
    setTeamResponsibleOptions(null)
    setSelectedHs(undefined)
    setSourcingOwner(undefined)
  }, [close])

  const onConfirm = useCallback(() => {
    if (!sourcingTeamUsers?.length) return
    dispatch(setLoading(true))
    reassignResponsible(selectedCC.uuid, {
      sourcing_owner: sourcingOwner ?? null,
      sourcing_team: sourcingTeamUsers
    })
      .then(() => {
        // discuss notification text with BA - not applicable to the current case
        notification.success({
          message: `Responsible has been successfully ${
            selectedCC.community_level_owner ? 'reassigned' : 'assigned'
          }`
        })
        close()
        onAssign()
      })
      .finally(() => {
        dispatch(setLoading(false))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCC, sourcingTeamUsers, sourcingOwner, dispatch])

  // Work principle:
  // calling get of hs list in 100% cases
  // if we have any team responsible users - we will take their hsId
  // and call available sourcing_team list of options based on it
  useEffect(() => {
    if (selectedCC && visible) {
      let activeHsId: string | undefined
      const promises = [fetchHSList()]
      // if category contain selected users and any of them has hsId
      if (selectedCC.sourcing_team?.length) {
        // getting hsID from team members
        activeHsId = selectedCC.sourcing_team.reduce<string | undefined>(
          (acc, responsible) => acc ?? responsible.health_system,
          undefined
        )
        if (activeHsId) {
          promises.push(fetchTeamResponsiblesList(activeHsId))
        }
      }
      dispatch(setLoading(true))
      Promise.all(promises).finally(() => {
        dispatch(setLoading(false))
        // setup initial values after fetch is completed
        const { sourcing_team, sourcing_owner } = selectedCC
        // if no hs id - the list will be empty for both options so no initial values
        if (activeHsId) {
          setSelectedHs(activeHsId)
          if (sourcing_owner) {
            setSourcingOwner(sourcing_owner.uuid)
          }
          if (sourcing_team) {
            setSourcingTeamUsers(sourcing_team.map((item) => item.uuid))
          }
        } else {
          setSelectedHs(undefined)
          setSourcingTeamUsers(undefined)
          setSourcingOwner(undefined)
        }
      })
    }
  }, [dispatch, fetchHSList, fetchTeamResponsiblesList, selectedCC, visible])

  const isSaveButtonDisabled =
    !sourcingTeamUsers?.length ||
    !selectedHs ||
    (!!selectedCC?.sourcing_owner && !sourcingOwner)

  if (!selectedCC) return null

  return (
    <Popup
      visible={visible}
      onCancel={onCancel}
      className="add-responsible-to-cc-popup__container"
      title={title}
      width={442}
      footer={
        <>
          <Button type={BUTTON_TYPES.DEFAULT} onClick={onCancel} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            form="contract-add-responsible"
            type={BUTTON_TYPES.PRIMARY}
            disabled={isSaveButtonDisabled}
            onClick={onConfirm}
            upperCase
          >
            {BTN_TXT.SAVE}
          </Button>
        </>
      }
    >
      <div className="column gap-24">
        <Typography.Body2 className="grey-caption">
          You are about to update Sourcing team and/or Sourcing owner for{' '}
          {selectedCC.name} contract category.
        </Typography.Body2>
        <Select
          options={hsOptions}
          propsItem={{
            label: 'Health system',
            required: true
          }}
          propsSelect={{
            getPopupContainer: (node) => document?.body ?? node,
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            onChange: onHsChange,
            value: selectedHs
          }}
          dropdownClassName="reassign-popup__select-dropdown"
          className="reassign-popup__select"
          alwaysName
        />
        <Select.Multi
          options={returnSelectUsers(filteredOptions)}
          propsItem={{
            label: 'Sourcing team',
            required: true
          }}
          propsSelect={{
            disabled: !selectedHs,
            // loading is handled by null value of teamResponsibleOptions
            loading: !teamResponsibleOptions,
            getPopupContainer: (node) => document?.body ?? node,
            filterSort: (optionA, optionB) =>
              optionA.label && optionB.label
                ? OPTIONS_SORT_ORDER[optionB.label as string] -
                  OPTIONS_SORT_ORDER[optionA.label as string]
                : 0,
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            onChange: onTeamResponsibleChangeChange,
            value: sourcingTeamUsers,
            filterOption: false,
            showSearch: true,
            onSearch: handleSearch
          }}
          dropdownClassName="reassign-popup__select-dropdown"
          className="reassign-popup__select"
          alwaysName
        />
        {!!sourcingOwnerOptions && (
          <Select
            options={sourcingOwnerOptions}
            propsItem={{
              label: 'Sourcing owner',
              required: true
            }}
            propsSelect={{
              disabled: !sourcingTeamUsers?.length,
              getPopupContainer: (node) => document?.body ?? node,
              placeholder: PLACEHOLDERS.PLEASE_SELECT,
              onChange: setSourcingOwner,
              value: sourcingOwner,
              filterOption: false
            }}
            dropdownClassName="reassign-popup__select-dropdown"
            className="reassign-popup__select"
            alwaysName
          />
        )}
      </div>
    </Popup>
  )
}
